import type { RaRecord } from "react-admin";

import {
  formatNarrationIts,
  formatNarrationRupee,
  formatNarrationTransactionId,
} from "./receipt-narration-format";

export type FmbReceiptNarrationTarget = "Annual" | "Contribution" | "Mixed" | "Credit";

export type FmbReceiptNarrationInput = {
  itsNo?: unknown;
  name?: unknown;
  amount?: unknown;
  paymentMode?: unknown;
  transactionId?: unknown;
  paymentRef?: unknown;
  target?: FmbReceiptNarrationTarget;
};

export type FmbReceiptNarrationOptions = {
  includeItsNo?: boolean;
  includeName?: boolean;
  includeAmount?: boolean;
  includePaymentMode?: boolean;
  includeTransactionId?: boolean;
  includeTarget?: boolean;
};

type FmbNarrationRecord = RaRecord | Record<string, unknown>;

function parseAmount(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

/** ITS, name, paid amount, payment mode, txn id, target — Tally export (not printed on receipt). */
export function formatFmbReceiptNarration(
  input: FmbReceiptNarrationInput,
  options?: FmbReceiptNarrationOptions
): string {
  const includeItsNo = options?.includeItsNo ?? true;
  const includeName = options?.includeName ?? true;
  const includeAmount = options?.includeAmount ?? true;
  const includePaymentMode = options?.includePaymentMode ?? true;
  const includeTransactionId = options?.includeTransactionId ?? true;
  const includeTarget = options?.includeTarget ?? true;

  const itsPart = includeItsNo ? formatNarrationIts(input.itsNo) : "";
  const name = includeName ? String(input.name ?? "").trim() : "";
  const paidAmount = includeAmount ? formatNarrationRupee(parseAmount(input.amount)) : "";
  const paymentMode = includePaymentMode
    ? String(input.paymentMode ?? "")
        .trim()
        .toUpperCase()
    : "";
  const txnPart = includeTransactionId
    ? formatNarrationTransactionId(input.transactionId ?? input.paymentRef)
    : "";
  const target = includeTarget ? String(input.target ?? "").trim() : "";

  return [itsPart, name, paidAmount, paymentMode, txnPart, target]
    .filter((part) => part !== "")
    .join(", ");
}

export function resolveFmbReceiptNarrationTarget(
  rec: FmbNarrationRecord
): FmbReceiptNarrationTarget {
  const lines = Array.isArray(rec.allocations) ? rec.allocations : [];
  let hasAnnual = false;
  let hasContribution = false;
  for (const line of lines) {
    const row = line as { fmbTakhmeenId?: unknown; fmbContributionId?: unknown };
    if (String(row.fmbContributionId ?? "").trim()) hasContribution = true;
    if (String(row.fmbTakhmeenId ?? "").trim()) hasAnnual = true;
  }
  const unallocated = parseAmount(rec.unallocatedAmount);
  if (unallocated > 0) hasAnnual = true;

  if (hasAnnual && hasContribution) return "Mixed";
  if (hasContribution) return "Contribution";
  if (hasAnnual) return "Annual";
  return "Credit";
}

export function formatFmbReceiptNarrationFromRecord(
  rec: FmbNarrationRecord,
  options?: FmbReceiptNarrationOptions
): string {
  const fmbData = rec.fmbData as
    | { itsNo?: string; name?: string; itsdata?: { Full_Name?: string } }
    | undefined;
  return formatFmbReceiptNarration(
    {
      itsNo: fmbData?.itsNo,
      name: fmbData?.itsdata?.Full_Name ?? fmbData?.name,
      amount: rec.amount,
      paymentMode: rec.paymentMode,
      transactionId: rec.transactionId,
      paymentRef: rec.paymentRef,
      target: resolveFmbReceiptNarrationTarget(rec),
    },
    options
  );
}
